import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Lightning, Warning, Package, CalendarBlank, CheckCircle, EnvelopeSimple, Megaphone, FacebookLogo, InstagramLogo, ChatCircleText, Copy, CircleNotch, Receipt, TrendDown, ChartLineUp, CalendarCheck, HourglassMedium, HandCoins, ArrowRight } from "@phosphor-icons/react";

const TAB_LABELS = {
  books: "Transactions",
  forecast: "Cash flow",
  receipts: "Receipts",
  diagnose: "Diagnose my books",
  portal: "Client portal",
  invoices: "Invoices",
  supplies: "Supplies",
  contractor: "Rentals & insurance",
  scheduler: "Bookings",
  accounting: "Reconcile",
  taxes: "Taxes",
  mileage: "Mileage",
  wellness_ops: "Review queue",
  ar: "Who owes you",
  ap: "Bills",
};

const ICONS = {
  low_stock: Package,
  out_of_stock: Warning,
  empty_day: CalendarBlank,
  open_slot: CalendarBlank,
  pending_receipts: Receipt,
  stale_scan: Receipt,
  no_scans: Receipt,
  cash_shortfall: TrendDown,
  cash_tight: ChartLineUp,
  cash_runway: HourglassMedium,
  bill_due: CalendarCheck,
  vendor_insight: HandCoins,
  anomaly_alert: Warning,
};

const SEV_COLOR = {
  high: "var(--bk-energy)",
  medium: "#E08A00",
  low: "var(--bk-accent)",
};

const CHANNELS = [
  { key: "facebook", label: "Facebook", icon: FacebookLogo },
  { key: "instagram", label: "Instagram", icon: InstagramLogo },
  { key: "text", label: "Text", icon: ChatCircleText },
];

export function ActiveAssistant({ businessId, onGoToTab }) {
  const [alerts, setAlerts] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [postAlert, setPostAlert] = useState(null);
  const [channel, setChannel] = useState("facebook");
  const [drafting, setDrafting] = useState(false);
  const [postText, setPostText] = useState("");

  useEffect(() => {
    let cancelled = false;
    api.get(`/businesses/${businessId}/alerts`).then((r) => {
      if (!cancelled) { setAlerts(r.data || []); setLoaded(true); }
    }).catch(() => setLoaded(true));
    return () => { cancelled = true; };
  }, [businessId]);

  if (!loaded) return null;

  const allClear = alerts.length === 0;

  const reorder = (a) => {
    const unit = a.unit ? ` ${a.unit}` : "";
    const subject = `Reorder request: ${a.supply_name}`;
    const body = `Hi${a.supplier_name ? " " + a.supplier_name : ""},\n\nWe'd like to reorder the following:\n\n• ${a.supply_name} — ${a.reorder_qty}${unit}\n\nPlease confirm availability and cost. Thank you!`;
    if (a.supplier_email) {
      window.location.href = `mailto:${a.supplier_email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      toast.success("Reorder email opened");
    } else {
      navigator.clipboard?.writeText(`${subject}\n\n${body}`)?.catch(() => {});
      toast.success("Reorder message copied — add a supplier email on the supply for 1-tap send");
    }
  };

  const generate = async (a, ch) => {
    setDrafting(true);
    setPostText("");
    try {
      const { data } = await api.post(`/businesses/${businessId}/draft-post`, {
        channel: ch, day: a.day || "", slot: a.slot || "", kind: a.type,
      });
      setPostText(data.post);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't draft a post.");
    } finally { setDrafting(false); }
  };

  const openPost = (a) => { setPostAlert(a); setChannel("facebook"); generate(a, "facebook"); };
  const switchChannel = (ch) => { setChannel(ch); if (postAlert) generate(postAlert, ch); };
  const copyPost = () => { navigator.clipboard?.writeText(postText)?.catch(() => {}); toast.success("Post copied — paste it anywhere"); };

  const openAlert = (a) => {
    if (!a.tab) return;
    onGoToTab?.(a.tab);
    setTimeout(() => {
      document.querySelector('[data-testid="business-tabs"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 120);
  };

  return (
    <div
      className="rounded-[28px] p-6 mb-8 bk-fade-up text-white relative overflow-hidden shadow-[0_12px_36px_rgba(26,39,68,0.18)]"
      style={{ background: "var(--bk-gradient-primary)" }}
      data-testid="active-assistant"
    >
      <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full" style={{ background: "rgba(255,255,255,0.12)" }} />
      <div className="flex items-center gap-2.5 mb-4 relative">
        <span className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: "rgba(255,255,255,0.2)" }}>
          <Lightning size={20} weight="fill" />
        </span>
        <div>
          <h3 className="font-display text-lg font-bold leading-tight">Needs your attention</h3>
          <p className="text-xs" style={{ color: "rgba(255,255,255,0.85)" }}>
            {allClear ? "Nothing needs you right now" : `${alerts.length} thing${alerts.length === 1 ? "" : "s"} to look at`}
          </p>
        </div>
      </div>

      {allClear ? (
        <div className="flex items-center gap-2 text-sm relative" data-testid="assistant-all-clear">
          <CheckCircle size={18} weight="fill" /> You're all caught up. Low stock and open appointment slots will show up here.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 relative">
          {alerts.map((a, i) => {
            const Icon = ICONS[a.type] || Warning;
            const isStock = a.type === "low_stock" || a.type === "out_of_stock";
            const isSlot = a.type === "empty_day" || a.type === "open_slot";
            return (
              <div
                key={i}
                data-testid={`assistant-alert-${i}`}
                className="text-left rounded-xl p-4 flex items-start gap-3"
                style={{ background: "rgba(255,255,255,0.14)", backdropFilter: "blur(4px)" }}
              >
                <div
                  onClick={() => openAlert(a)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === "Enter") openAlert(a); }}
                  className="flex items-start gap-3 min-w-0 flex-1 text-left cursor-pointer transition-transform hover:-translate-y-0.5"
                  data-testid={`assistant-alert-go-${i}`}
                >
                  <span className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: "#fff", color: SEV_COLOR[a.severity] || "var(--bk-energy)" }}>
                    <Icon size={17} weight="fill" />
                  </span>
                  <span className="min-w-0">
                    <span className="block font-semibold text-sm">{a.title}</span>
                    <span className="block text-xs mt-0.5" style={{ color: "rgba(255,255,255,0.88)" }}>{a.detail}</span>
                    <span className="flex flex-wrap items-center gap-2 mt-2">
                      {isStock && (
                        <button onClick={(e) => { e.stopPropagation(); reorder(a); }} data-testid={`assistant-reorder-${i}`} className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full" style={{ background: "#fff", color: "var(--bk-energy)" }}>
                          <EnvelopeSimple size={13} weight="bold" /> Restock in one tap
                        </button>
                      )}
                      {isSlot && (
                        <button onClick={(e) => { e.stopPropagation(); openPost(a); }} data-testid={`assistant-draftpost-${i}`} className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full" style={{ background: "#fff", color: "var(--bk-accent)" }}>
                          <Megaphone size={13} weight="bold" /> Draft social post
                        </button>
                      )}
                      {a.tab && (
                        <span data-testid={`assistant-open-tab-${i}`} className="inline-flex items-center gap-1 text-xs font-bold px-3 py-1.5 rounded-full" style={{ background: "rgba(255,255,255,0.22)", color: "#fff" }}>
                          {a.action_label || `Open ${TAB_LABELS[a.tab] || a.tab}`} <ArrowRight size={12} weight="bold" />
                        </span>
                      )}
                    </span>
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={!!postAlert} onOpenChange={(o) => { if (!o) { setPostAlert(null); setPostText(""); } }}>
        <DialogContent className="sm:max-w-lg rounded-[28px] border-0 shadow-[0_24px_60px_rgba(26,39,68,0.18)]" data-testid="draftpost-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ color: "var(--bk-text)" }}>
              <Megaphone size={20} weight="duotone" color="var(--bk-accent)" /> Draft a social post
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm" style={{ color: "var(--bk-text-2)" }}>
            Rowan writes a catchy, anonymous post to fill {postAlert?.day || "your open slots"}. Pick a channel, then copy &amp; paste.
          </p>
          <div className="flex items-center gap-2">
            {CHANNELS.map((c) => (
              <button key={c.key} onClick={() => switchChannel(c.key)} data-testid={`draftpost-channel-${c.key}`} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-semibold border transition-colors" style={channel === c.key ? { background: "var(--bk-accent)", color: "#fff", borderColor: "var(--bk-accent)" } : { color: "var(--bk-text-2)", borderColor: "var(--bk-border)" }}>
                <c.icon size={15} weight="duotone" /> {c.label}
              </button>
            ))}
          </div>
          <div className="rounded-lg border p-4 min-h-[120px] text-sm whitespace-pre-wrap" style={{ borderColor: "var(--bk-border)", background: "var(--bk-page)", color: "var(--bk-text)" }} data-testid="draftpost-output">
            {drafting ? (
              <span className="inline-flex items-center gap-2" style={{ color: "var(--bk-text-2)" }}><CircleNotch size={16} className="animate-spin" /> Drafting your post…</span>
            ) : postText || "…"}
          </div>
          <div className="flex justify-between gap-2">
            <button onClick={() => switchChannel(channel)} disabled={drafting} data-testid="draftpost-regenerate" className="text-sm font-semibold px-4 py-2 rounded-full border disabled:opacity-60" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }}>
              Regenerate
            </button>
            <button onClick={copyPost} disabled={drafting || !postText} data-testid="draftpost-copy" className="bk-btn-primary inline-flex items-center gap-2 text-sm font-semibold px-5 py-2 rounded-full disabled:opacity-60">
              <Copy size={15} weight="bold" /> Copy post
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
